import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Sliding-pill tab bar for the Home page. Same interaction as Inventory's TabBar
 * (a single indicator measured off the active button, rather than each button
 * toggling its own background) but in the blue Support palette, since this bar sits
 * above the Home dashboard rather than inside the amber Inventory module.
 */
const HomeTabBar = ({ tabs, activeTab, onChange }) => {
    const tabRefs = useRef({});
    const [indicator, setIndicator] = useState(null);

    useLayoutEffect(() => {
        const el = tabRefs.current[activeTab];
        if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }, [activeTab, tabs.length]);

    useEffect(() => {
        const handleResize = () => {
            const el = tabRefs.current[activeTab];
            if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeTab]);

    // A single tab is just a label with no choice to make — don't render the bar.
    if (tabs.length < 2) return null;

    return (
        <div className="relative mb-6 flex gap-1 overflow-x-auto rounded-2xl border border-inv-cream-200 bg-white p-1.5 shadow-sm">
            {indicator && (
                <div
                    className="absolute top-1.5 bottom-1.5 rounded-xl bg-inv-brand shadow-[0_8px_18px_-10px_rgba(194,87,26,0.7)] transition-all duration-300 ease-out"
                    style={{ left: indicator.left, width: indicator.width }}
                />
            )}
            {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                    <button
                        key={tab.key}
                        ref={(el) => { tabRefs.current[tab.key] = el; }}
                        onClick={() => onChange(tab.key)}
                        className={`relative z-10 flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                            active ? 'text-white' : 'text-inv-muted hover:text-inv-ink'
                        }`}
                    >
                        <Icon size={16} />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

export default HomeTabBar;
